"use client";

import { useEffect, useRef, useState } from "react";
import { Package } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PackagingDesignProductDevelopment() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const packages = [{
    id: 1,
    name: "Premium Coffee Package",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1438489/img/premium_coffee_package_design.png",
    description: "Elegant packaging design for specialty coffee brands"
  }, {
    id: 2,
    name: "Organic Skincare Box",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1438489/img/organic_skincare_box_design.png",
    description: "Sustainable packaging solution for beauty products"
  }];
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };
    container.addEventListener("mousemove", handleMouseMove);
    const animate = () => {
      if (!canvas.width || !canvas.height) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gradient = ctx.createRadialGradient(mousePos.x, mousePos.y, 0, mousePos.x, mousePos.y, 200);
      gradient.addColorStop(0, "rgba(255, 255, 255, 0.15)");
      gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.05)");
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      requestAnimationFrame(animate);
    };
    if (canvas.width > 0 && canvas.height > 0) {
      animate();
    }
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      container.removeEventListener("mousemove", handleMouseMove);
    };
  }, [mousePos.x, mousePos.y]);
  return <section ref={sectionRef} className="relative bg-dark-background text-foreground-dark py-24 overflow-hidden">
      <div ref={containerRef} className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-10" style={{
        mixBlendMode: "screen"
      }} />

        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-4">
            <Package className="w-5 h-5" />
            <span className="text-sm font-semibold">Packaging Excellence</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
            Packaging Design & Product Development
          </h2>
          <p className="text-foreground-dark/80 text-lg max-w-2xl mx-auto">
            Transform your products with innovative packaging solutions that
            captivate customers and elevate your brand presence
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 relative z-20">
          {packages.map((pkg, i) => <div key={pkg.id} data-index={i} className={`group relative transition-all duration-700 delay-${i * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 150}ms`,
          perspective: "1000px"
        }}>
              <div className="relative bg-muted-dark-background rounded-lg overflow-hidden transition-all duration-500 ease-out-back hover:scale-105" style={{
            transformStyle: "preserve-3d",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.2)"
          }}>
                <div className="relative aspect-[4/3] overflow-hidden transition-transform duration-700 ease-out-back group-hover:rotate-y-12 group-hover:-rotate-x-6" style={{
              transformStyle: "preserve-3d"
            }}>
                  <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-background/80 via-dark-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                boxShadow: "inset 0 -40px 60px rgba(0, 0, 0, 0.5)"
              }} />
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-display font-bold mb-2 text-foreground-dark">
                    {pkg.name}
                  </h3>
                  <p className="text-foreground-dark/70 leading-relaxed">
                    {pkg.description}
                  </p>
                </div>

                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
              boxShadow: "0 30px 90px rgba(0, 0, 0, 0.5), 0 12px 36px rgba(0, 0, 0, 0.3)",
              filter: "blur(20px)",
              transform: "translateZ(-50px)"
            }} />
              </div>
            </div>)}
        </div>

        <div className={`mt-16 text-center transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-foreground-dark/70 mb-6 max-w-xl mx-auto">
            From concept to production, we create packaging that tells your
            brand story and stands out on shelves
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="bg-muted-dark-background px-6 py-3 rounded-lg border border-border/20">
              <span className="text-accent font-semibold">3D Mockups</span>
            </div>
            <div className="bg-muted-dark-background px-6 py-3 rounded-lg border border-border/20">
              <span className="text-accent font-semibold">Material Selection</span>
            </div>
            <div className="bg-muted-dark-background px-6 py-3 rounded-lg border border-border/20">
              <span className="text-accent font-semibold">Print Ready Files</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}